import { createContext, useContext, useId, useState, type ReactNode } from 'react';
import { ArrowBigUp, Bookmark, BookmarkCheck, Loader2, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { CommentAuthor, CommunityFeedItem, CommunityFeedTitle, Title, TitleType } from '@shared/types';

import { PaletteAvatar } from '@/components/PaletteAvatar';
import { PerspectiveCover } from '@/components/PerspectiveCover';
import { Poster } from '@/components/Poster';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { COMMENT_MAX_LENGTH, newMutationId, useCommentVote, usePostReply } from '@/features/discussion/api';
import type { FriendActivityItem } from '@/features/friends/api';
import { activityVerbKey, newestWatchedAt, relativeTime, seasonEpisodeLabel } from '@/features/friends/feed';
import { useSetStatus } from '@/features/show/api';
import { localized } from '@/i18n/localized';
import { useLocale } from '@/i18n/locale';
import { cn } from '@/lib/utils';
import { titlePath } from '@/lib/title-path';

import type { FeedCard } from './feed-model';
import { commentTarget, threadLink } from './links';

/*
 * The Tonight timeline (ui-ux.md §3 "Open timeline row"): no boxes — each
 * row is avatar (36px) · one sentence (names and titles in ink, the verb in
 * ink-secondary) · a quiet meta line · an optional quote · the title's 44px
 * poster on the right of a row capped at 640px (so it stays near the text).
 * Rows are separated by space and wash `surface-alt` on hover. The whole row is a link (an `absolute inset-0` overlay under the
 * content; links and buttons inside sit above it): to the title for a watch,
 * to the thread for a comment. Per-row actions are icon buttons with
 * tooltips, revealed on hover / focus-within and always shown on touch —
 * never the same labelled button repeated down the list.
 */

/** Revealed on row hover / keyboard focus inside the row; always on touch. */
const REVEAL =
  'opacity-0 transition-opacity duration-fast group-hover/row:opacity-100 group-focus-within/row:opacity-100 touch:opacity-100';

type Translate = (key: string, params?: Record<string, string | number>) => string;

function authorName(t: Translate, author?: CommentAuthor | null): string {
  if (!author) return t('friends.someone');
  return author.displayName?.trim() || author.username;
}

interface PosterThumbTitle {
  id: string;
  type?: TitleType;
  title: string;
  titleBr?: string;
  palette: [string, string];
  posterUrl?: string | null;
  posterFallbackUrl?: string | null;
}

/** The row's 44px cover — decoration inside the row link, so it takes no clicks of its own. */
function PosterThumb({ title }: { title: PosterThumbTitle }) {
  const { locale } = useLocale();
  return (
    <div aria-hidden className="pointer-events-none w-11 shrink-0 self-start">
      <Poster
        id={title.id}
        name={localized(title.title, title.titleBr, locale)}
        palette={title.palette}
        imageUrl={title.posterUrl ?? undefined}
        imageFallbackUrl={title.posterFallbackUrl ?? undefined}
        sizes="44px"
        layout={false}
        fallbackTitle={false}
      />
    </div>
  );
}

/** 36px avatar → the person's profile. A pointer target only: the name link is the keyboard stop. */
function AvatarLink({ author }: { author?: CommentAuthor | null }) {
  const { t } = useLocale();
  const name = authorName(t, author);
  const avatar = (
    <PaletteAvatar
      name={name}
      avatarUrl={author?.avatarUrl ?? null}
      palette={author?.avatarPalette ?? ['#334155', '#0F172A']}
      className="size-9"
      alt=""
    />
  );
  if (!author?.username) return <span aria-hidden>{avatar}</span>;
  return (
    <Link
      to={`/user/${author.username}`}
      tabIndex={-1}
      aria-hidden
      className="relative self-start rounded-full"
    >
      {avatar}
    </Link>
  );
}

function NameLink({ author }: { author?: CommentAuthor | null }) {
  const { t } = useLocale();
  const name = authorName(t, author);
  if (!author?.username) return <span className="font-medium text-ink">{name}</span>;
  return (
    <Link
      to={`/user/${author.username}`}
      className="relative font-medium text-ink underline-offset-4 hover:underline"
    >
      {name}
    </Link>
  );
}

function TitleLink({
  title,
}: {
  title: { id: string; title: string; titleBr?: string; type?: TitleType; titleType?: TitleType };
}) {
  const { locale } = useLocale();
  return (
    <Link
      to={titlePath(title.id, title.type ?? title.titleType)}
      className="relative font-medium text-ink underline-offset-4 hover:underline"
    >
      {localized(title.title, title.titleBr, locale)}
    </Link>
  );
}

/**
 * The row frame: avatar | body | poster, over the row link. `rowLink.label`
 * set = the overlay is a real tab stop (a comment row's thread, which no
 * in-sentence link reaches); unset = a pointer target only (a watch row's
 * title, which the sentence already links).
 */
function Row({
  avatar,
  poster,
  children,
  label,
  to,
  linkLabel,
}: {
  avatar?: ReactNode;
  poster?: ReactNode;
  children: ReactNode;
  label?: string;
  to: string;
  linkLabel?: string;
}) {
  return (
    <article
      aria-label={label}
      className={cn(
        // Capped at 640px so the poster stays beside the text instead of
        // stranded at the far edge of a wide column.
        'group/row relative -mx-3 grid max-w-[640px] items-start gap-3 rounded-lg px-3 py-1.5 transition-colors duration-fast hover:bg-surface-alt',
        avatar ? 'grid-cols-[36px_minmax(0,1fr)_44px]' : 'grid-cols-[minmax(0,1fr)_44px]',
      )}
    >
      <Link
        to={to}
        aria-label={linkLabel}
        aria-hidden={linkLabel ? undefined : true}
        tabIndex={linkLabel ? undefined : -1}
        className="absolute inset-0 rounded-lg focus-visible:-outline-offset-2"
      />
      {avatar}
      <div className="min-w-0">{children}</div>
      {poster}
    </article>
  );
}

function Sentence({ children }: { children: ReactNode }) {
  return <p className="text-subhead text-ink-secondary">{children}</p>;
}

/** Quiet facts (separate spans, spaced — no "A · B" strings), then the row's actions. */
function Meta({ children, actions }: { children: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mt-0.5 flex min-h-6 flex-wrap items-center gap-x-3 gap-y-1 text-caption text-ink-tertiary">
      {children}
      {actions ? <div className="relative -my-1 ml-auto flex items-center gap-1">{actions}</div> : null}
    </div>
  );
}

function When({ iso }: { iso: string }) {
  const { locale } = useLocale();
  return <time dateTime={iso}>{relativeTime(iso, Date.now(), locale)}</time>;
}

function Code({ season, episode }: { season: number; episode: number }) {
  const { t } = useLocale();
  return <span className="text-timecode">{t('episode.code', { season, episode })}</span>;
}

/** An icon-only row action: accessible name + matching tooltip. */
function RowAction({
  label,
  onClick,
  disabled,
  expanded,
  className,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  expanded?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-expanded={expanded}
          disabled={disabled}
          onClick={onClick}
          className={cn('touch-target-min h-8 w-8', className)}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** The friend's stars, `rating` colour (a friend's score, read-only). */
function FriendStars({ rating }: { rating: number }) {
  const { t } = useLocale();
  return (
    <span
      role="img"
      aria-label={t('tonight.stars', { rating })}
      className="whitespace-nowrap tracking-tight text-rating"
    >
      {'★'.repeat(rating)}
      <span className="text-ink-tertiary">{'★'.repeat(5 - rating)}</span>
    </span>
  );
}

/**
 * Title ids already in the user's library. Provided once by the screen (one
 * `useLibrary` observer) rather than read per row — an observer mounting with
 * every row would refetch a failed library query and flicker Up next.
 */
const OwnedTitlesContext = createContext<ReadonlySet<string> | ReadonlyMap<string, unknown>>(new Set());

export function OwnedTitlesProvider({
  owned,
  children,
}: {
  owned: ReadonlySet<string> | ReadonlyMap<string, unknown>;
  children: ReactNode;
}) {
  return <OwnedTitlesContext.Provider value={owned}>{children}</OwnedTitlesContext.Provider>;
}

/** "+ Watchlist" for a title outside the user's library (nothing when it is already in). */
function WatchlistAction({ title }: { title: Title }) {
  const { locale, t } = useLocale();
  const statusMap = useContext(OwnedTitlesContext);
  const setStatus = useSetStatus(title.id, title.type);
  const [added, setAdded] = useState(false);
  const name = localized(title.title, title.titleBr, locale);
  if (statusMap.has(title.id) && !added) return null;
  return (
    <RowAction
      label={added ? t('tonight.inWatchlistA11y', { title: name }) : t('tonight.addWatchlistA11y', { title: name })}
      disabled={added || setStatus.isPending}
      onClick={() => setStatus.mutate('watchlist', { onSuccess: () => setAdded(true) })}
      className={added ? 'opacity-100' : REVEAL}
    >
      {added ? (
        <BookmarkCheck className="size-4" strokeWidth={1.75} aria-hidden />
      ) : (
        <Bookmark className="size-4" strokeWidth={1.75} aria-hidden />
      )}
    </RowAction>
  );
}

// --- activity rows --------------------------------------------------------------

function WatchedSingle({ item }: { item: FriendActivityItem }) {
  const { t } = useLocale();
  const hasCode =
    (item.kind === 'episode' || item.kind === 'rewatch') &&
    item.seasonNumber !== undefined &&
    item.episodeNumber !== undefined;
  return (
    <Row
      to={titlePath(item.title.id, item.title.type)}
      avatar={<AvatarLink author={item.friend} />}
      poster={<PosterThumb title={item.title} />}
    >
      <Sentence>
        <NameLink author={item.friend} /> <span>{t(activityVerbKey(item.kind))}</span> <TitleLink title={item.title} />
      </Sentence>
      <Meta actions={<WatchlistAction title={item.title} />}>
        {hasCode ? <Code season={item.seasonNumber!} episode={item.episodeNumber!} /> : null}
        {item.kind === 'movie' ? <span>{t('common.film')}</span> : null}
        <When iso={item.watchedAt} />
      </Meta>
    </Row>
  );
}

/** Up to this many distinct episodes are named; beyond it the sentence carries the count alone. */
const CODES_SHOWN = 2;

function WatchedGroup({ items }: { items: FriendActivityItem[] }) {
  const { t } = useLocale();
  const first = items[0];
  // Episode order (S4E1, S4E2, …) — watch timestamps from a bulk import can
  // tie or arrive out of order, so sort by season/episode, not by time.
  const episodes = items
    .filter((entry) => entry.seasonNumber !== undefined && entry.episodeNumber !== undefined)
    .map((entry) => [entry.seasonNumber!, entry.episodeNumber!] as const)
    .sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const distinct: (readonly [number, number])[] = [];
  const seen = new Set<string>();
  for (const pair of episodes) {
    const label = seasonEpisodeLabel(pair[0], pair[1]);
    if (!seen.has(label)) {
      seen.add(label);
      distinct.push(pair);
    }
  }
  return (
    <Row
      to={titlePath(first.title.id, first.title.type)}
      avatar={<AvatarLink author={first.friend} />}
      poster={<PosterThumb title={first.title} />}
    >
      <Sentence>
        <NameLink author={first.friend} />{' '}
        <span>{t('tonight.watchedEpisodesOf', { count: items.length })}</span>{' '}
        <TitleLink title={first.title} />
      </Sentence>
      <Meta actions={<WatchlistAction title={first.title} />}>
        {distinct.length <= CODES_SHOWN
          ? distinct.map(([season, episode]) => (
              <Code key={`${season}-${episode}`} season={season} episode={episode} />
            ))
          : null}
        <When iso={newestWatchedAt(items)} />
      </Meta>
    </Row>
  );
}

function RatedRow({ item }: { item: FriendActivityItem }) {
  const { t } = useLocale();
  const hasCode =
    item.kind === 'episode' && item.seasonNumber !== undefined && item.episodeNumber !== undefined;
  return (
    <Row
      to={titlePath(item.title.id, item.title.type)}
      avatar={<AvatarLink author={item.friend} />}
      poster={<PosterThumb title={item.title} />}
    >
      <Sentence>
        <NameLink author={item.friend} /> <span>{t('tonight.verb.rated')}</span> <TitleLink title={item.title} />{' '}
        <FriendStars rating={item.rating ?? 0} />
      </Sentence>
      <Meta actions={<WatchlistAction title={item.title} />}>
        {item.kind === 'title' ? <span>{t('tonight.finishedSeries')}</span> : null}
        {item.kind === 'movie' ? <span>{t('common.film')}</span> : null}
        {hasCode ? <Code season={item.seasonNumber!} episode={item.episodeNumber!} /> : null}
        <When iso={item.watchedAt} />
      </Meta>
    </Row>
  );
}

// --- comment rows -----------------------------------------------------------------

function feedTitleThumb(title: CommunityFeedTitle): PosterThumbTitle {
  return {
    id: title.id,
    type: title.titleType,
    title: title.title,
    titleBr: title.titleBr,
    palette: title.palette,
    posterUrl: title.posterUrl,
    posterFallbackUrl: title.posterFallbackUrl,
  };
}

/** The title, plus the episode code for episode comments. */
function TargetLabel({ item }: { item: CommunityFeedItem }) {
  const target = commentTarget(item.comment);
  return (
    <>
      <TitleLink title={item.title} />
      {target?.titleType === 'episode' ? (
        <>
          {' '}
          <span className="text-timecode text-caption text-ink-tertiary">
            <Code season={target.seasonNumber ?? 0} episode={target.episodeNumber ?? 0} />
          </span>
        </>
      ) : null}
    </>
  );
}

/** Quoted speech — the one place a 2px rule is the marker (ui-ux.md §3). */
function Quote({ body }: { body: string }) {
  return (
    <blockquote className="prose-measure m-0 mt-2 line-clamp-4 whitespace-pre-line break-words border-l-2 border-border pl-3 text-body text-ink">
      {body}
    </blockquote>
  );
}

/** Reply count as a quiet icon + number; the row link's name carries it for assistive tech. */
function ReplyCount({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span aria-hidden className="inline-flex items-center gap-1">
      <MessageCircle className="size-3.5" strokeWidth={1.75} />
      <span className="tabular-nums">{count}</span>
    </span>
  );
}

/** Upvote with local optimistic state (the vote hook patches thread caches). Always visible: it carries the score. */
function VoteButton({ item }: { item: CommunityFeedItem }) {
  const { t } = useLocale();
  const target = commentTarget(item.comment) ?? {
    titleType: item.title.titleType,
    tmdbId: item.title.id,
  };
  const vote = useCommentVote(target);
  const base = { voted: item.comment.viewerVoted, score: item.comment.score };
  const [override, setOverride] = useState<{ base: typeof base; voted: boolean; score: number } | null>(
    null,
  );
  const current =
    override && override.base.voted === base.voted && override.base.score === base.score
      ? override
      : base;
  const toggle = () => {
    const voted = !current.voted;
    setOverride({ base, voted, score: Math.max(0, current.score + (voted ? 1 : -1)) });
    vote.mutate(
      { commentId: item.comment.commentId, voted },
      {
        onSuccess: (result) => setOverride({ base, voted: result.voted, score: result.score }),
        onError: () => setOverride(null),
      },
    );
  };
  return (
    <Button
      variant="ghost"
      size="sm"
      aria-pressed={current.voted}
      aria-label={t('tonight.upvoteA11y', { count: current.score })}
      onClick={toggle}
      className={cn('touch-target-min h-8 gap-1 px-2', current.voted && 'text-ink')}
    >
      <ArrowBigUp className={cn('size-4', current.voted && 'fill-current')} strokeWidth={1.75} aria-hidden />
      <span className="tabular-nums">{current.score}</span>
    </Button>
  );
}

/** The inline reply composer under a friend's comment (`usePostReply`). */
function ReplyComposer({
  item,
  onDone,
  onCancel,
}: {
  item: CommunityFeedItem;
  onDone: () => void;
  onCancel: () => void;
}) {
  const { t } = useLocale();
  const fieldId = useId();
  const target = commentTarget(item.comment) ?? {
    titleType: item.title.titleType,
    tmdbId: item.title.id,
  };
  const reply = usePostReply(target);
  const [body, setBody] = useState('');
  const trimmed = body.trim();
  const name = authorName(t, item.friend ?? item.comment.author);
  const submit = () => {
    if (!trimmed || reply.isPending) return;
    reply.mutate(
      { mutationId: newMutationId(), parentCommentId: item.comment.commentId, body: trimmed },
      { onSuccess: onDone },
    );
  };
  return (
    <form
      className="relative mt-3 flex flex-col gap-2"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <label htmlFor={fieldId} className="text-caption text-ink-secondary">
        {t('tonight.replyTo', { name })}
      </label>
      <Textarea
        id={fieldId}
        value={body}
        autoFocus
        maxLength={COMMENT_MAX_LENGTH}
        onChange={(event) => setBody(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            submit();
          }
          if (event.key === 'Escape') onCancel();
        }}
        className="min-h-[72px] bg-bg"
      />
      <div className="flex items-center gap-2">
        <Button type="submit" variant="neutral" size="sm" disabled={!trimmed || reply.isPending}>
          {reply.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          {t('tonight.postReply')}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

function CommentedRow({ item }: { item: CommunityFeedItem }) {
  const { t } = useLocale();
  const [composing, setComposing] = useState(false);
  const [replied, setReplied] = useState(false);
  const author = item.friend ?? item.comment.author;
  const replies = item.comment.replyCount + (replied ? 1 : 0);
  return (
    <Row
      to={threadLink(commentTarget(item.comment), item.title.id)}
      linkLabel={replies > 0 ? t('tonight.viewThreadCount', { count: replies }) : t('tonight.viewThread')}
      avatar={<AvatarLink author={author} />}
      poster={<PosterThumb title={feedTitleThumb(item.title)} />}
    >
      <Sentence>
        <NameLink author={author} /> <span>{t('friends.commentedOn')}</span> <TargetLabel item={item} />
      </Sentence>
      {item.comment.body ? <Quote body={item.comment.body} /> : null}
      <Meta
        actions={
          <>
            <VoteButton item={item} />
            <RowAction
              label={t('tonight.reply')}
              expanded={composing}
              onClick={() => setComposing((open) => !open)}
              className={composing ? 'opacity-100' : REVEAL}
            >
              <MessageCircle className="size-4" strokeWidth={1.75} aria-hidden />
            </RowAction>
          </>
        }
      >
        <When iso={item.comment.createdAt} />
        <ReplyCount count={replies} />
      </Meta>
      {composing ? (
        <ReplyComposer
          item={item}
          onCancel={() => setComposing(false)}
          onDone={() => {
            setComposing(false);
            setReplied(true);
          }}
        />
      ) : null}
      {replied && !composing ? (
        <p role="status" className="mt-2 text-caption text-ink-secondary">
          {t('tonight.replyPosted')}
        </p>
      ) : null}
    </Row>
  );
}

function HotRow({ item }: { item: CommunityFeedItem }) {
  const { t } = useLocale();
  const author = item.comment.author;
  const replies = item.comment.replyCount;
  return (
    <Row
      to={threadLink(commentTarget(item.comment), item.title.id)}
      linkLabel={replies > 0 ? t('tonight.viewThreadCount', { count: replies }) : t('tonight.viewThread')}
      label={t('tonight.hotOnShelf')}
      avatar={<AvatarLink author={author} />}
      poster={<PosterThumb title={feedTitleThumb(item.title)} />}
    >
      <Sentence>
        <NameLink author={author} /> <span>{t('friends.commentedOn')}</span> <TargetLabel item={item} />
      </Sentence>
      {item.comment.body ? <Quote body={item.comment.body} /> : null}
      <Meta actions={<VoteButton item={item} />}>
        <span>{t('tonight.hotOnShelf')}</span>
        <When iso={item.comment.createdAt} />
        <ReplyCount count={replies} />
      </Meta>
    </Row>
  );
}

/** "Because you finished X": an inline mini rail of perspective cases, no box. */
function BecauseRail({ source, titles }: { source: Title; titles: Title[] }) {
  const { locale, t } = useLocale();
  const sourceName = localized(source.title, source.titleBr, locale);
  return (
    <section aria-label={t('tonight.becauseA11y', { title: sourceName })} className="py-3">
      <p className="text-subhead text-ink-secondary">
        {t('tonight.becauseYouFinished')}{' '}
        <Link
          to={titlePath(source.id, source.type)}
          className="font-medium text-ink underline-offset-4 hover:underline"
        >
          {sourceName}
        </Link>
      </p>
      {/* Perspective cases (hover pose); the track's padding is room for a
          turned case's grown top/bottom edge inside the scroll box. */}
      <ul className="scrollbar-none m-0 -mb-3 flex list-none snap-x gap-3 overflow-x-auto px-0 py-3">
        {titles.map((title) => {
          const name = localized(title.title, title.titleBr, locale);
          return (
            <li key={title.id} className="w-[88px] shrink-0 snap-start sm:w-[104px]">
              <PerspectiveCover variant="hover">
                <Link
                  to={titlePath(title.id, title.type)}
                  aria-label={t('common.openTitle', { name })}
                  className="block"
                >
                  <Poster
                    id={title.id}
                    name={name}
                    palette={title.palette}
                    imageUrl={title.posterUrl}
                    imageFallbackUrl={title.posterFallbackUrl}
                    sizes="(min-width: 640px) 104px, 88px"
                    layout={false}
                    lift={false}
                  />
                </Link>
              </PerspectiveCover>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** One timeline row, by kind. */
export function FeedCardView({ card }: { card: FeedCard }) {
  switch (card.kind) {
    case 'watched':
      return card.row.kind === 'group' ? (
        <WatchedGroup items={card.row.items} />
      ) : (
        <WatchedSingle item={card.row.item} />
      );
    case 'rated':
      return <RatedRow item={card.item} />;
    case 'commented':
      return <CommentedRow item={card.item} />;
    case 'hot':
      return <HotRow item={card.item} />;
    case 'because':
      return <BecauseRail source={card.source} titles={card.titles} />;
  }
}
